import { useState } from 'react';
import {
  ArrowLeft, Plus, FileDown, Lock, AlertTriangle, CheckCircle2, X,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import ChipEstado from '../ui/ChipEstado';
import { ESTADO_ACCION, ESTADO_NC, SEVERIDAD_NC } from '../ui/estados';
import {
  DISCIPLINAS_8D, DISPOSICIONES_NC, ESTADOS_NC_EDITABLES, SEVERIDADES_NC,
} from '../ui/calidad';
import { useAuth } from '../lib/contextoAuth';

// LA FICHA DE UNA NO CONFORMIDAD · Fase 4 del módulo de Calidad.
//
// Es donde se contesta «¿qué hicieron para que no vuelva a pasar?», así
// que está ordenada como esa respuesta: qué pasó, qué se hizo con el
// material, por qué pasó, y qué se cambió.
//
// TRES DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Cada disciplina lleva escrito qué va dentro.** «D5» a secas es
//      una casilla que cada quien llena con lo que cree. Quien va a
//      llenar esto no ha hecho un 8D en su vida.
//   2. **Cerrar avisa de la consecuencia y comprueba antes.** Es
//      irreversible, y no se deja si quedan acciones pendientes — se
//      dice cuáles, no «no se puede».
//   3. **Una cerrada se ve cerrada.** Sin botones que den 403: los
//      formularios desaparecen y en su lugar hay una frase que explica
//      que se abre otra si aparece algo nuevo.

function Campo({ etiqueta, ayuda, valor, onCambiar, filas = 3, bloqueado }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-gray-900">{etiqueta}</span>
      <span className="block text-xs text-gray-500 mb-1">{ayuda}</span>
      <textarea
        value={valor || ''} rows={filas} disabled={bloqueado}
        onChange={(e) => onCambiar(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
      />
    </label>
  );
}

// ---------------------------------------------------------------------
// Las ocho disciplinas
// ---------------------------------------------------------------------
function OchoD({ nc, onGuardado, bloqueado }) {
  const inicial = () => {
    const base = {};
    DISCIPLINAS_8D.forEach(([campo]) => {
      base[campo] = (nc.reporte_8d || {})[campo] || '';
    });
    return base;
  };
  const [campos, setCampos] = useState(inicial);
  const [porques, setPorques] = useState(
    () => (nc.porques || []).map((p) => p.texto));
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);

  async function guardar() {
    setError(null);
    setOk(false);
    setGuardando(true);
    try {
      const d = await api.put(`/no-conformidades/${nc.id}/ocho-d`, {
        ...campos, porques: porques.filter((p) => p.trim()),
      });
      onGuardado(d);
      setOk(true);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  const llenas = DISCIPLINAS_8D.filter(([c]) => (campos[c] || '').trim()).length;

  return (
    <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-4">
      <div>
        <h2 className="font-semibold text-gray-900">Análisis 8D</h2>
        <p className="text-sm text-gray-600">
          Las ocho cosas que un cliente industrial espera leer, en este
          orden. <strong>{llenas} de 8</strong> llenas. Se llena en semanas:
          no hace falta tenerlo todo hoy.
        </p>
      </div>

      {DISCIPLINAS_8D.map(([campo, etiqueta, ayuda]) => (
        <div key={campo} className="space-y-2">
          <Campo
            etiqueta={etiqueta} ayuda={ayuda} valor={campos[campo]}
            bloqueado={bloqueado}
            onCambiar={(v) => setCampos((a) => ({ ...a, [campo]: v }))}
          />

          {/* LOS PORQUÉS VAN DENTRO DE D4, que es donde se usan. En una
              sección aparte al final, nadie los relaciona con la causa
              raíz que acaba de escribir tres centímetros más arriba. */}
          {campo === 'd4_causa_raiz' && (
            <div className="ml-4 pl-3 border-l-2 border-gray-200 space-y-2">
              <div className="text-xs text-gray-600">
                Los porqués, en orden. Empieza por lo que se vio y pregunta
                «¿y por qué?» hasta llegar a algo que se pueda cambiar.
              </div>
              {porques.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-4">{i + 1}.</span>
                  <input
                    value={p} disabled={bloqueado}
                    onChange={(e) => setPorques(
                      (a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                  />
                  {!bloqueado && (
                    <button type="button" aria-label={`Quitar el porqué ${i + 1}`}
                            onClick={() => setPorques((a) => a.filter((_, j) => j !== i))}
                            className="text-gray-400 hover:text-red-600">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              {!bloqueado && porques.length < 10 && (
                <button
                  type="button" onClick={() => setPorques((a) => [...a, ''])}
                  className="text-sm font-medium text-[var(--color-marca)] hover:underline"
                >
                  + Añadir un porqué
                </button>
              )}
            </div>
          )}

          {campo === 'd7_prevencion' && (nc.reporte_8d || {}).documento_codigo && (
            <div className="ml-4 text-xs text-gray-600">
              Documento actualizado:{' '}
              <strong>{nc.reporte_8d.documento_codigo}</strong>{' '}
              «{nc.reporte_8d.documento_titulo}», revisión{' '}
              {nc.reporte_8d.documento_revision}
            </div>
          )}
        </div>
      ))}

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}
      {ok && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          Análisis guardado.
        </div>
      )}

      {!bloqueado && (
        <button
          onClick={guardar} disabled={guardando}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar el análisis'}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------
// Las acciones correctivas
// ---------------------------------------------------------------------
function Acciones({ nc, onCambio, bloqueado, personas }) {
  const [abierto, setAbierto] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [responsable, setResponsable] = useState('');
  const [fecha, setFecha] = useState('');
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const hoy = new Date().toISOString().slice(0, 10);

  async function crear(e) {
    e.preventDefault();
    setError(null);
    try {
      const d = await api.post(`/no-conformidades/${nc.id}/acciones`, {
        descripcion,
        id_responsable: responsable ? Number(responsable) : null,
        fecha_compromiso: fecha || null,
      });
      onCambio(d);
      setAviso(d.aviso || null);
      setDescripcion(''); setResponsable(''); setFecha('');
      setAbierto(false);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    }
  }

  async function marcar(accion, estado) {
    setError(null);
    const cuerpo = { estado };
    if (estado === 'CANCELADA') {
      // CANCELAR EXIGE MOTIVO, y se pide antes de mandar: recibir un 422
      // después de pulsar es peor que preguntarlo.
      const motivo = window.prompt(
        'Una acción que desaparece sin explicación es la que nadie hizo.\n'
        + '¿Por qué se cancela?');
      if (!motivo || !motivo.trim()) return;
      cuerpo.nota_cierre = motivo.trim();
    }
    try {
      onCambio(await api.put(`/no-conformidades/acciones/${accion.id}`, cuerpo));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    }
  }

  return (
    <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
      <div>
        <h2 className="font-semibold text-gray-900">Acciones correctivas</h2>
        <p className="text-sm text-gray-600">
          Con responsable y fecha, o no es una acción: es un buen propósito.
          Mientras quede alguna pendiente, esta no conformidad no se puede
          cerrar.
        </p>
      </div>

      {nc.acciones.length === 0 && (
        <p className="text-sm text-gray-500">
          Sin acciones. Si el problema no necesita ninguna acción,
          se puede cerrar igual; escribe el motivo en la D8.
        </p>
      )}

      {nc.acciones.map((a) => {
        const vencida = a.estado === 'PENDIENTE' && a.fecha_compromiso
          && a.fecha_compromiso < hoy;
        return (
          <div key={a.id}
               className={`border rounded-xl p-3 ${vencida ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-gray-900">{a.descripcion}</div>
                <div className="text-xs text-gray-600">
                  {a.responsable || 'Sin responsable'}
                  {a.fecha_compromiso ? ` · para el ${a.fecha_compromiso}` : ' · sin fecha'}
                  {vencida && ' · YA SE PASÓ'}
                </div>
                {a.nota_cierre && (
                  <div className="text-xs text-gray-600 mt-1">{a.nota_cierre}</div>
                )}
              </div>
              <ChipEstado mapa={ESTADO_ACCION} clave={a.estado} />
            </div>
            {!bloqueado && a.estado === 'PENDIENTE' && (
              <div className="flex gap-3 mt-2">
                <button onClick={() => marcar(a, 'HECHA')}
                        className="text-sm font-semibold text-[var(--color-marca)] hover:underline">
                  Marcar como hecha
                </button>
                <button onClick={() => marcar(a, 'CANCELADA')}
                        className="text-sm text-gray-600 hover:underline">
                  Cancelarla
                </button>
              </div>
            )}
          </div>
        );
      })}

      {aviso && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {!bloqueado && (abierto ? (
        <form onSubmit={crear} className="border border-gray-200 rounded-xl p-3 space-y-3 bg-gray-50">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700">Qué se va a hacer</span>
            <input
              value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Cambiar el herramental de la prensa"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Quién</span>
              <select value={responsable} onChange={(e) => setResponsable(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Elige a alguien —</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre_completo}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Para cuándo</span>
              <input
                type="date" value={fecha} min={hoy}
                onChange={(e) => setFecha(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={descripcion.trim().length < 5}
                    className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              Añadir la acción
            </button>
            <button type="button" onClick={() => setAbierto(false)}
                    className="text-sm text-gray-600 hover:underline">Cancelar</button>
            {descripcion.trim().length < 5 && (
              <span className="text-sm text-gray-500">Falta decir qué se va a hacer.</span>
            )}
          </div>
        </form>
      ) : (
        <button onClick={() => { setAbierto(true); setAviso(null); }}
                className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm font-semibold text-gray-800">
          <Plus size={16} /> Añadir una acción correctiva
        </button>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------
// Cerrar
// ---------------------------------------------------------------------
function Cerrar({ nc, onCerrada }) {
  const [abierto, setAbierto] = useState(false);
  const [disposicion, setDisposicion] = useState(nc.disposicion || '');
  const [cierre, setCierre] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const pendientes = nc.acciones_pendientes;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      onCerrada(await api.post(`/no-conformidades/${nc.id}/cerrar`, {
        disposicion, cierre,
      }));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cerrar.');
    } finally {
      setEnviando(false);
    }
  }

  // EL BOTÓN DESHABILITADO DICE POR QUÉ Y CUÁNTAS (Doctrina 2, regla 8).
  if (pendientes > 0) {
    return (
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 text-sm text-gray-700">
        <strong>No se puede cerrar.</strong> Quedan {pendientes}{' '}
        acción(es) correctiva(s) pendiente(s). Márcalas como hechas —o
        cancélalas explicando por qué— arriba. Cerrar con acciones a medias
        es como se quedan sin hacer para siempre: esto deja de aparecer en
        las listas.
      </div>
    );
  }

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
      >
        <CheckCircle2 size={16} /> Cerrar la no conformidad
      </button>
    );
  }

  return (
    <form onSubmit={enviar} className="border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3">
      <div className="flex gap-2 text-amber-900">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <strong>Cerrar no se puede deshacer.</strong> Este 8D se le entrega
          a alguien, así que a partir de aquí no se modifica ni se reabre. Si
          más adelante aparece algo nuevo, se abre otra no conformidad.
        </div>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">
          Qué se hizo con el material
        </span>
        <select value={disposicion} onChange={(e) => setDisposicion(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">— Elige —</option>
          {DISPOSICIONES_NC.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Cómo quedó</span>
        <textarea value={cierre} onChange={(e) => setCierre(e.target.value)} rows={3}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <span className="block text-xs text-gray-500 mt-1">
          Es la D8, y es lo último que lee quien revise este papel dentro de
          un año. Si dejaste la D8 en blanco, esto se guarda ahí.
        </span>
      </label>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!disposicion || cierre.trim().length < 10 || enviando}
                className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {enviando ? 'Cerrando…' : 'Cerrar definitivamente'}
        </button>
        <button type="button" onClick={() => setAbierto(false)}
                className="text-sm text-gray-600 hover:underline">Todavía no</button>
        {!disposicion && <span className="text-sm text-gray-500">Falta decir qué se hizo con el material.</span>}
        {disposicion && cierre.trim().length < 10 && (
          <span className="text-sm text-gray-500">Falta escribir cómo quedó.</span>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// La ficha
// ---------------------------------------------------------------------
export default function FichaNoConformidad({ inicial, onVolver, onCambio, personas }) {
  const { puede } = useAuth();
  const [nc, setNc] = useState(inicial);
  const [aviso, setAviso] = useState(inicial.mensaje || null);
  const [error, setError] = useState(null);
  const [bajando, setBajando] = useState(false);
  const [reporte, setReporte] = useState(null);

  const cerrada = nc.estado === 'CERRADA';
  const resuelve = puede('calidad.no_conformidad_resolver');
  const bloqueado = cerrada || !resuelve;

  function refrescar(d) {
    setNc(d);
    setAviso(d.mensaje || null);
    if (onCambio) onCambio();
  }

  async function cambiar(campo, valor) {
    setError(null);
    try {
      refrescar(await api.put(`/no-conformidades/${nc.id}`, { [campo]: valor }));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    }
  }

  // EL PDF SE ENTREGA POR URL FIRMADA, como el reporte de trazabilidad:
  // la autorización es la propia firma de la URL, así que se enlaza y no
  // se descarga con `api.bajar`. Es al revés que los adjuntos de un
  // documento controlado, que sí van por cabecera — y por eso conviene
  // mirar cuál es cada uno antes de copiar el patrón de al lado.
  async function generarPdf() {
    setError(null);
    setBajando(true);
    try {
      const d = await api.post(`/no-conformidades/${nc.id}/reporte`, {});
      setReporte(d);
      setAviso(d.mensaje);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo generar.');
    } finally {
      setBajando(false);
    }
  }

  return (
    <div className="space-y-5">
      <button onClick={onVolver}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline">
        <ArrowLeft size={16} /> Volver a las no conformidades
      </button>

      <header className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-gray-900">
            No conformidad {nc.folio}
          </h1>
          <ChipEstado mapa={ESTADO_NC} clave={nc.estado} />
          <ChipEstado mapa={SEVERIDAD_NC} clave={nc.severidad} />
        </div>
        <p className="text-sm text-gray-600">
          {nc.origen_nombre} · detectada el {nc.fecha}
          {nc.detectada_por_nombre ? ` por ${nc.detectada_por_nombre}` : ''}
          {nc.sku ? ` · ${nc.sku} ${nc.producto}` : ''}
        </p>
      </header>

      {aviso && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {cerrada && (
        <div className="flex gap-2 text-sm text-gray-700 bg-gray-100 border border-gray-200 rounded-xl p-3">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>Cerrada</strong>
            {nc.cerrada_por_nombre ? ` por ${nc.cerrada_por_nombre}` : ''}. Su
            8D ya se le entregó a alguien: no se modifica ni se
            reabre. Si apareció algo nuevo, abre otra y menciona ésta.
          </div>
        </div>
      )}

      <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
        <h2 className="font-semibold text-gray-900">Qué pasó</h2>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{nc.descripcion}</p>

        {nc.lotes.length > 0 && (
          <div className="text-sm text-gray-700">
            <span className="font-medium">Lotes afectados: </span>
            {nc.lotes.map((l) => (
              <span key={l.id} className="mr-2">
                {l.codigo_lote}{l.cantidad ? ` (${l.cantidad} pzas)` : ''}
              </span>
            ))}
          </div>
        )}

        {!bloqueado && (
          <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Gravedad</span>
              <select value={nc.severidad} onChange={(e) => cambiar('severidad', e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {SEVERIDADES_NC.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Por dónde va</span>
              <select value={nc.estado} onChange={(e) => cambiar('estado', e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {ESTADOS_NC_EDITABLES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Qué se hizo con el material
              </span>
              <select value={nc.disposicion || ''}
                      onChange={(e) => cambiar('disposicion', e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Sin decidir —</option>
                {DISPOSICIONES_NC.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
          </div>
        )}

        {bloqueado && nc.disposicion_nombre && (
          <div className="text-sm text-gray-700">
            <span className="font-medium">Qué se hizo con el material: </span>
            {nc.disposicion_nombre}
          </div>
        )}
      </section>

      <OchoD nc={nc} onGuardado={refrescar} bloqueado={bloqueado} />
      <Acciones nc={nc} onCambio={refrescar} bloqueado={bloqueado}
                personas={personas} />

      <div className="flex flex-wrap gap-3">
        {resuelve && !cerrada && <Cerrar nc={nc} onCerrada={refrescar} />}
        {resuelve && (
          <button
            onClick={generarPdf} disabled={bajando}
            className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm font-semibold text-gray-800 disabled:opacity-50"
          >
            <FileDown size={16} />
            {bajando ? 'Generando…' : 'Sacar el 8D en PDF'}
          </button>
        )}
      </div>

      {reporte && (
        <div className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1">
          <a href={reporte.url} className="underline font-semibold">
            Descargar el 8D (reporte {reporte.folio})
          </a>
          <div className="text-xs text-gray-600">
            Huella del contenido:{' '}
            <code className="font-mono">{reporte.huella_contenido?.slice(0, 16)}…</code>
            {' — '}con los mismos datos vuelve a salir la misma y
            quien lo reciba puede comprobar que sigue diciendo lo mismo.
          </div>
        </div>
      )}
      {resuelve && (
        <p className="text-xs text-gray-500">
          El PDF se puede sacar aunque el análisis esté a medias: los
          apartados vacíos lo dicen en vez de desaparecer, que es lo que hay
          que mandar cuando el cliente pregunta por dónde van.
        </p>
      )}
    </div>
  );
}
